import { useRef, useState, useLayoutEffect } from "react";
import { Link } from "react-router-dom";
import CareIllustration from "./CareIllustration";

const services = [
  {
    kind: "kidney",
    title: "Kidney care",
    headline: "A clearer path for your kidney health.",
    summary: "Find a nephrologist and bring your health story together.",
    action: "Find a nephrologist",
    to: "/patient/doctors?specialty=Nephrology",
    steps: [
      [
        "Find your specialist",
        "Explore nephrology profiles and consultation details.",
      ],
      [
        "Choose a visit",
        "Review available dates, your preferred time and the final price.",
      ],
      [
        "Keep your care connected",
        "Return to your bookings and records for your next visit.",
      ],
    ],
    prepare: [
      [
        "Your health history",
        "Have your current concerns and previous diagnoses ready to discuss.",
      ],
      [
        "Existing reports",
        "Keep any recent reports and prescriptions available.",
      ],
      [
        "Your questions",
        "Write down what you would like to discuss with your doctor.",
      ],
    ],
  },
  {
    kind: "doctor",
    title: "Doctor consultation",
    headline: "Meet the person behind your care.",
    summary: "Explore profiles, choose a time and book your consultation.",
    action: "Find a doctor",
    to: "/patient/doctors",
    steps: [
      [
        "Get to know your doctor",
        "Review qualifications, specialties, clinic details and fees.",
      ],
      ["Find a time that fits", "Choose a listed date and consultation time."],
      [
        "Review, then book",
        "Check your details and the final price before confirming.",
      ],
    ],
    prepare: [
      [
        "Who is the appointment for?",
        "Have the patient name and contact number ready.",
      ],
      [
        "A reason for your visit",
        "A short description helps explain what you need.",
      ],
      [
        "Relevant documents",
        "Keep your current prescriptions and reports together.",
      ],
    ],
  },
  {
    kind: "lab",
    title: "Lab tests",
    headline: "Your next test, without the guesswork.",
    summary:
      "Compare available labs, add tests and choose your collection time.",
    action: "Explore lab tests",
    to: "/patient/tests",
    steps: [
      [
        "Choose your tests",
        "Search the catalogue or upload a prescription for review.",
      ],
      [
        "Choose an available lab",
        "Review partner prices and the test’s preparation information.",
      ],
      [
        "Book and follow along",
        "Confirm your collection preference and track your booking.",
      ],
    ],
    prepare: [
      [
        "Your test or prescription",
        "Use the exact test name, or a clear image of the prescription.",
      ],
      [
        "Preparation requirements",
        "Read the instructions supplied with your selected test.",
      ],
      [
        "A suitable collection time",
        "Check the available dates and locations before booking.",
      ],
    ],
  },
  {
    kind: "heart",
    title: "Ongoing care",
    headline: "Pick up where your last visit left off.",
    summary: "Keep appointments, your care team and follow-ups in view.",
    action: "View my bookings",
    to: "/patient/bookings",
    steps: [
      [
        "See what is coming up",
        "Find your upcoming consultations and laboratory bookings.",
      ],
      ["Review your last visit", "Open booking details and available reports."],
      [
        "Plan your next step",
        "Find your doctor and arrange another visit when needed.",
      ],
    ],
    prepare: [
      [
        "Your patient account",
        "Sign in with the account you used for your bookings.",
      ],
      [
        "Your booking reference",
        "Use it to identify the visit you want to review.",
      ],
      [
        "Questions since your visit",
        "Keep a note of anything you want to discuss at your next consultation.",
      ],
    ],
  },
  {
    kind: "report",
    title: "Health records",
    headline: "Less searching. More clarity.",
    summary: "Keep personal documents and available lab reports within reach.",
    action: "Open my records",
    to: "/patient/documents",
    steps: [
      [
        "Bring your records together",
        "Upload a document or prescription to your health profile.",
      ],
      [
        "Find the right report",
        "View your personal uploads and reports attached to lab bookings.",
      ],
      [
        "Prepare for your next visit",
        "Open the information you need when speaking with your care team.",
      ],
    ],
    prepare: [
      ["A clear file", "Choose a readable JPG, PNG or PDF, up to 5 MB."],
      [
        "The right category",
        "Separate prescriptions from your other medical documents.",
      ],
      ["Your patient account", "Your records are available after you sign in."],
    ],
  },
  {
    kind: "membership",
    title: "Memberships",
    headline: "Make regular care easier to plan.",
    summary:
      "Compare current plans and review the benefits that matter to you.",
    action: "Compare memberships",
    to: "/patient/memberships",
    steps: [
      [
        "Explore current plans",
        "Read each plan’s benefits, price and duration.",
      ],
      [
        "Make an informed choice",
        "Review the plan details and payment amount before joining.",
      ],
      [
        "Keep your membership handy",
        "Find your membership status and details in your account.",
      ],
    ],
    prepare: [
      [
        "Your care needs",
        "Think about which listed benefits you expect to use.",
      ],
      ["Plan conditions", "Read the duration, exclusions and refund policy."],
      [
        "Your account details",
        "Check that your name and contact details are up to date.",
      ],
    ],
  },
];

export default function ServiceExplorer() {
  const [selected, setSelected] = useState(null);
  const [view, setView] = useState("steps");
  const dialog = useRef(null);
  useLayoutEffect(() => {
    if (dialog.current?.open) {
      dialog.current.scrollTop = 0;
      dialog.current
        .querySelector(".nc-dialog-close")
        ?.focus({ preventScroll: true });
    }
  }, [selected]);
  function choose(service) {
    setSelected(service);
    setView("steps");
  }
  function open(service) {
    choose(service);
    dialog.current.showModal();
  }
  const close = () => dialog.current.close();
  return (
    <>
      <section id="care" className="nc-section container nc-services">
        <div className="nc-section-heading">
          <div>
            <span className="nc-eyebrow">YOUR CARE, AT YOUR PACE</span>
            <h2>What would you like to do today?</h2>
          </div>
          <p>
            Find your next step.
            <br />
            We’ll help you make it a little simpler.
          </p>
        </div>
        <div className="nc-service-grid">
          {services.map((item, index) => (
            <button
              key={item.kind}
              className="nc-service-tile"
              onClick={() => open(item)}
              aria-label={`Explore ${item.title}`}
            >
              <div className="nc-service-tile-top">
                <span className="nc-eyebrow">{item.title}</span>
                <span className="nc-service-number">0{index + 1}</span>
              </div>
              <CareIllustration kind={item.kind} />
              <h3>{item.headline}</h3>
              <p>{item.summary}</p>
              <span className="nc-service-link">
                See your next steps <span aria-hidden="true">→</span>
              </span>
            </button>
          ))}
        </div>
      </section>
      <dialog
        ref={dialog}
        className="nc-service-dialog"
        aria-labelledby="service-title"
        onClick={(event) => {
          if (event.target === dialog.current) close();
        }}
      >
        <div className="nc-service-close">
          <button
            className="nc-dialog-close"
            aria-label="Close service preview"
            onClick={close}
          >
            ×
          </button>
        </div>
        {selected && (
          <>
            <div className="nc-service-preview" key={selected.kind}>
              <div className="nc-service-visual">
                <span className="nc-eyebrow">{selected.title}</span>
                <CareIllustration kind={selected.kind} />
                <h2 id="service-title">{selected.headline}</h2>
                <p>{selected.summary}</p>

              </div>
              <div className="nc-service-detail">
                <span className="nc-eyebrow">LET’S MAKE IT SIMPLE</span>
                <h3>Your next step starts here.</h3>
                <div
                  className="nc-preview-switch"
                  aria-label="Service information"
                >
                  <button
                    aria-pressed={view === "steps"}
                    onClick={() => setView("steps")}
                  >
                    How it works
                  </button>
                  <button
                    aria-pressed={view === "prepare"}
                    onClick={() => setView("prepare")}
                  >
                    What to prepare
                  </button>
                </div>
                <ol className="nc-service-steps" key={view}>
                  {selected[view].map(([title, description], index) => (
                    <li key={title}>
                      <span>{view === "steps" ? `0${index + 1}` : "✓"}</span>
                      <div>
                        <h4>{title}</h4>
                        <p>{description}</p>
                      </div>
                    </li>
                  ))}
                </ol>
                <Link className="nc-button" to={selected.to} onClick={close}>
                  {selected.action}
                  <span aria-hidden="true">→</span>
                </Link>
                <p className="nc-service-signin">
                  Continue in your patient workspace. Sign in to manage your
                  care.
                </p>
              </div>
            </div>
            <nav
              className="nc-preview-services"
              aria-label="Explore another service"
            >
              <span>Explore another service</span>
              <div>
                {services.map((item) => (
                  <button
                    key={item.kind}
                    aria-pressed={selected.kind === item.kind}
                    onClick={() => choose(item)}
                  >
                    {item.title}
                  </button>
                ))}
              </div>
            </nav>
          </>
        )}
      </dialog>
    </>
  );
}
